/**
 * Rules for the calibre image gallery, shared by the calibre editor and the
 * reference editor's "Copy to calibre" action.
 *
 * The cap and the copy rule were both about to exist twice — once in the modal
 * that owns the gallery, once in the reference editor that now writes into it —
 * which is how two screens come to disagree about how many images a calibre may
 * hold. One definition, both callers.
 */

/**
 * How many images one calibre may carry.
 *
 * Lived as a private `MAX_IMAGES` inside `CustomCalibreModal` until a second
 * screen needed to respect it. The value is unchanged.
 */
export const MAX_CALIBRE_IMAGES = 3;

/** The tag that marks a reference image as showing the movement. */
export const MOVEMENT_IMAGE_TAG = 'movement';

/**
 * May this reference image be offered a "Copy to calibre" action?
 *
 * Both halves have to hold, and both are answered from state the reference
 * editor already has — no reads:
 *
 *  - the reference is LINKED to a calibre, because an unlinked ref has no
 *    destination and the copy would be a guess about which movement this is;
 *  - the image is tagged `movement`, because the rest of a reference's gallery
 *    is dial shots and wrist shots, and a calibre gallery full of those helps
 *    nobody.
 *
 * Deliberately not a check on the calibre's remaining capacity: capacity is
 * knowable only at write time (see `calibreImageCopyOutcome`), and hiding the
 * action on a stale count would leave an operator with no way to find out why.
 */
export function canCopyImageToCalibre(input: {
  /** The ref's `calibreId`, and whether it resolved to a real calibre. */
  calibreId: string | null | undefined;
  calibreResolved: boolean;
  /** Tags on the gallery image. */
  tags: readonly string[] | undefined;
}): boolean {
  if (!input.calibreId?.trim()) return false;
  if (!input.calibreResolved) return false;
  return (input.tags ?? []).some(
    (t) => String(t ?? '').trim().toLowerCase() === MOVEMENT_IMAGE_TAG,
  );
}

/** What a copy attempt should do, given the calibre's images right now. */
export type CalibreImageCopyOutcome = 'ok' | 'at-max';

/**
 * Decided against the calibre's CURRENT images, re-read at click time.
 *
 * The reference editor holds a copy of the linked calibre from when it opened,
 * which is fine for deciding whether to show the action and useless for
 * deciding whether the write fits: another screen may have filled the gallery
 * since. Trusting the stale copy would write a fourth image past a cap the
 * calibre editor enforces, and nothing downstream would notice.
 *
 * Duplicates are deliberately NOT detected. Telling one copy from another would
 * mean recording the source image on the calibre record, and `CalibreImage` has
 * nowhere to put it — the field would be schema drift outside shared-types for a
 * mistake that costs one click to undo.
 */
export function calibreImageCopyOutcome(
  // Only ever counted, so typed as "some array" rather than pinned to one of the
  // two structurally-identical CalibreImage declarations (shared-types' and
  // firebase.ts's own). Counting is the whole job; the element type is not this
  // module's business.
  currentImages: readonly unknown[] | undefined,
): CalibreImageCopyOutcome {
  return (currentImages?.length ?? 0) >= MAX_CALIBRE_IMAGES ? 'at-max' : 'ok';
}

/** The message shown when the gallery is full. */
export const CALIBRE_GALLERY_FULL_MESSAGE = `Calibre gallery at max (${MAX_CALIBRE_IMAGES} images). Delete one first.`;
